import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import styled from 'styled-components';

import { TagButton } from '@sorare/core/src/atoms/buttons/TagButton';
import { useUpdateSubscription } from '@sorare/core/src/hooks/subscriptions/useUpdateSubscription';
import {
  blockchainRarities,
  formatScarcity,
  subscribableRarities,
  usSportPlayableRarities,
  usSportSubscribableRarities,
} from '@sorare/core/src/lib/cards';
import { isType } from '@sorare/core/src/lib/gql';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';

import { AnyPlayerDescription } from 'components/mySorare/common/AnyPlayerDescription';

import type { PlayerNotifications_subscription } from './__generated__/index.graphql';

interface Props {
  subscription: PlayerNotifications_subscription;
}

const playableRaritiesByType: Record<string, string[] | readonly string[]> = {
  Player: blockchainRarities,
  BaseballPlayer: usSportPlayableRarities,
  NBAPlayer: usSportPlayableRarities,
};

const subscribableRaritiesByType: Record<string, string[]> = {
  Player: subscribableRarities,
  BaseballPlayer: usSportSubscribableRarities,
  NBAPlayer: usSportSubscribableRarities,
};

const Root = styled.div`
  @media ${laptopAndAbove} {
    display: flex;
    justify-content: space-between;
  }
`;
const Scarcities = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  padding: var(--unit) 0;
`;

export const PlayerNotifications = ({ subscription }: Props) => {
  const updateSubscription = useUpdateSubscription(subscription);

  if (
    !(
      isType(subscription.anySubscribable, 'Player') ||
      isType(subscription.anySubscribable, 'NBAPlayer') ||
      isType(subscription.anySubscribable, 'BaseballPlayer')
    )
  ) {
    return null;
  }

  return (
    <Root>
      <AnyPlayerDescription player={subscription.anySubscribable} />
      <Scarcities>
        {playableRaritiesByType[subscription.anySubscribable.__typename].map(
          scarcity => {
            const selected =
              subscription.preferences.notifyForRarities.includes(scarcity);
            const disabled =
              !subscribableRaritiesByType[
                subscription.anySubscribable.__typename
              ].includes(scarcity);
            return (
              <TagButton
                key={scarcity}
                selected={selected}
                disabled={disabled}
                onClick={() => {
                  updateSubscription([scarcity], !selected);
                }}
              >
                {formatScarcity(scarcity)}
              </TagButton>
            );
          }
        )}
      </Scarcities>
    </Root>
  );
};

PlayerNotifications.fragments = {
  subscription: gql`
    fragment PlayerNotifications_subscription on EmailSubscription {
      slug
      preferences {
        slug
        notifyForRarities
      }
      anySubscribable {
        slug
        ...AnyPlayerDescription_anyPlayer
      }
    }
    ${AnyPlayerDescription.fragments.anyPlayer}
  ` as TypedDocumentNode<PlayerNotifications_subscription>,
};
